import React, { useState, useEffect, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Autocomplete } from 'ui/components/Autocomplete/Autocomplete'; // Custom Autocomplete
import MultiSelect from 'ui/components/Autocomplete/MultiSelect/MultiSelect'; // Added import
import DraggableColumnList from 'ui/components/DraggableList/DraggableColumnList'; // Added import
import CompactColorPicker from 'ui/components/CompactColorPicker/CompactColorPicker';

import {
  AdvancedTableData,
  AdvancedTableHeader,
  AdvancedTableCellData, // Added import
  // DynamicField,
} from '../../../types';
import { AVAILABLE_ADVANCED_TABLE_COLUMNS } from '../consts';

const FONT_FAMILY_OPTIONS = [
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, sans-serif' },
  { label: 'Trebuchet MS', value: "'Trebuchet MS', sans-serif" },
  { label: 'Times New Roman', value: "'Times New Roman', serif" },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Courier New', value: "'Courier New', monospace" },
  { label: 'Lucida Console', value: "'Lucida Console', monospace" },
];

interface AdvancedTablePropertiesProps {
  advancedTableProps: AdvancedTableData;
  updateElementProperty: (key: string, value: any) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  canvasWidth?: number; // Width of the canvas/page
  currentElementWidth?: number; // Current width of the selected table element
}

const AdvancedTableProperties = ({
  advancedTableProps,
  updateElementProperty,
  canvasWidth,
  currentElementWidth,
}: AdvancedTablePropertiesProps) => {
  const [localHeaderFontSize, setLocalHeaderFontSize] = useState<string>(
    advancedTableProps.headerFontSize?.toString() || ''
  );
  const [localCellFontSize, setLocalCellFontSize] = useState<string>(
    advancedTableProps.defaultCellFontSize?.toString() || ''
  );

  useEffect(() => {
    setLocalHeaderFontSize(advancedTableProps.headerFontSize?.toString() || '');
  }, [advancedTableProps.headerFontSize]);

  useEffect(() => {
    setLocalCellFontSize(
      advancedTableProps.defaultCellFontSize?.toString() || ''
    );
  }, [advancedTableProps.defaultCellFontSize]);
  const handleFontSizeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    isHeader: boolean
  ) => {
    const value = event.target.value;
    const key = isHeader ? 'headerFontSize' : 'defaultCellFontSize';

    // Update local state for a responsive input field
    if (isHeader) {
      setLocalHeaderFontSize(value);
    } else {
      setLocalCellFontSize(value);
    }

    // Now, update the global state for live canvas updates
    if (value === '') {
      const newAdvancedTableData = { ...advancedTableProps, [key]: undefined };
      updateElementProperty('advancedTableData', newAdvancedTableData);
    } else {
      const newSize = Number.parseInt(value, 10);
      if (!isNaN(newSize) && newSize >= 0) {
        // Start with the font size update
        let newAdvancedTableData: AdvancedTableData = {
          ...advancedTableProps,
          [key]: newSize,
        };

        // Define a padding constant and line height multiplier for height calculation
        const PADDING = 8;
        const LINE_HEIGHT_MULTIPLIER = 1.2;

        // Adjust row height based on which font size changed
        if (isHeader) {
          const newHeaderRowHeight = Math.round(
            newSize * LINE_HEIGHT_MULTIPLIER + PADDING
          );
          newAdvancedTableData = {
            ...newAdvancedTableData,
            headerRowHeight: newHeaderRowHeight,
          };
        } else {
          const newCellRowHeight = Math.round(
            newSize * LINE_HEIGHT_MULTIPLIER + PADDING
          );
          const newRowHeights = Array(advancedTableProps.numRows).fill(
            newCellRowHeight
          );
          newAdvancedTableData = {
            ...newAdvancedTableData,
            rowHeights: newRowHeights,
          };
        }

        updateElementProperty('advancedTableData', newAdvancedTableData);
      }
    }
  };

  const handleHeaderBackgroundColorChange = (newColor: string) => {
    const newAdvancedTableData = {
      ...advancedTableProps,
      headerBackgroundColor: newColor,
    };
    updateElementProperty('advancedTableData', newAdvancedTableData);
  };

  const handleCellTextColorChange = (newColor: string) => {
    const newAdvancedTableData = {
      ...advancedTableProps,
      defaultCellTextColor: newColor,
    };
    updateElementProperty('advancedTableData', newAdvancedTableData);
  };

  const handleHeaderTextColorChange = (newColor: string) => {
    const newAdvancedTableData = {
      ...advancedTableProps,
      headerTextColor: newColor,
    };
    updateElementProperty('advancedTableData', newAdvancedTableData);
  };

  const handleCellBackgroundColorChange = (newColor: string) => {
    const newAdvancedTableData = {
      ...advancedTableProps,
      defaultCellBackgroundColor: newColor,
    };
    updateElementProperty('advancedTableData', newAdvancedTableData);
  };

  const handleHeaderFontFamilyChange = (
    selectedOption: { label: string; value: string } | null
  ) => {
    const newAdvancedTableData = {
      ...advancedTableProps,
      headerFontFamily: selectedOption ? selectedOption.value : undefined,
    };
    updateElementProperty('advancedTableData', newAdvancedTableData);
  };

  const handleCellFontFamilyChange = (
    selectedOption: { label: string; value: string } | null
  ) => {
    const newAdvancedTableData = {
      ...advancedTableProps,
      defaultCellFontFamily: selectedOption ? selectedOption.value : undefined,
    };
    updateElementProperty('advancedTableData', newAdvancedTableData);
  };

  const handleShowGridChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    const newAdvancedTableData = {
      ...advancedTableProps,
      showGrid: checked,
    };
    updateElementProperty('advancedTableData', newAdvancedTableData); // Update global element state
  };

  const widthPercentageOptions = useMemo(
    () =>
      Array.from({ length: 10 }, (_, i) => ({
        label: `${(i + 1) * 10}%`,
        value: (i + 1) * 10,
      })),
    []
  );

  const handleWidthPercentageChange = (
    selectedOption: { label: string; value: number } | null
  ) => {
    if (!selectedOption || !canvasWidth || !advancedTableProps) return;

    const selectedPercentageValue = selectedOption.value;
    const newAbsoluteTableWidth = (selectedPercentageValue / 100) * canvasWidth;

    const columnWidthsArray = advancedTableProps.columnWidths ?? [];

    const currentTotalColumnWidth = columnWidthsArray.reduce(
      (sum, cw) => sum + cw,
      0
    );

    const newScaledColumnWidths =
      currentTotalColumnWidth > 0
        ? columnWidthsArray.map(
            (cw) => (cw / currentTotalColumnWidth) * newAbsoluteTableWidth
          )
        : columnWidthsArray.map(
            () => newAbsoluteTableWidth / (columnWidthsArray.length || 1)
          );

    const newAdvancedTableData: AdvancedTableData = {
      ...advancedTableProps,
      columnWidths: newScaledColumnWidths,
    };

    // Create a compound update object
    const compoundUpdate = {
      width: newAbsoluteTableWidth,
      advancedTableData: newAdvancedTableData,
    };

    // Send the compound update
    updateElementProperty('advancedTableCompound', compoundUpdate);
  };

  const handleColumnSelectionChange = (
    selectedHeaders: AdvancedTableHeader[] | null
  ) => {
    if (!advancedTableProps) return;

    const newHeaders = selectedHeaders || [];
    const oldHeaders = advancedTableProps.headers || [];
    const newNumColumns = newHeaders.length;

    // Preserve existing data and restructure for new column set
    const newTableData: (AdvancedTableCellData | null)[][] = [];
    const numRows = advancedTableProps.numRows || 0; // Use existing numRows or default to 0

    for (let i = 0; i < numRows; i++) {
      const newRow: (AdvancedTableCellData | null)[] = [];
      const oldRow = advancedTableProps.data?.[i] || [];
      for (const newHeader of newHeaders) {
        const oldHeaderIndex = oldHeaders.findIndex(
          (h) => h.key === newHeader.key
        );
        if (oldHeaderIndex !== -1 && oldRow[oldHeaderIndex]) {
          newRow.push(oldRow[oldHeaderIndex]);
        } else {
          newRow.push({ content: '' }); // Default content for new cells
        }
      }
      newTableData.push(newRow);
    }

    // Adjust column widths
    // Simple redistribution: if there's an existing total width (from currentElementWidth),
    // distribute it among new columns. Otherwise, default to 100px per column.
    let newColumnWidths: number[] = [];
    const currentTableWidth = currentElementWidth || newNumColumns * 100; // Fallback width

    if (newNumColumns > 0) {
      const defaultWidth = currentTableWidth / newNumColumns;
      newColumnWidths = newHeaders.map((header) => {
        const oldHeaderDef = oldHeaders.find((h) => h.key === header.key);
        const oldHeaderIndex = oldHeaderDef
          ? oldHeaders.indexOf(oldHeaderDef)
          : -1;
        if (
          oldHeaderIndex !== -1 &&
          advancedTableProps.columnWidths?.[oldHeaderIndex]
        ) {
          // Attempt to preserve old width if column existed - this might need refinement
          // For now, let's just redistribute for simplicity to avoid overly complex logic here
          // Consider a more sophisticated width preservation strategy later if needed.
          return defaultWidth; // Simplified: equal distribution
        }
        return defaultWidth; // Default width for new columns or if old width not found
      });
    } else {
      newColumnWidths = [];
    }

    // Ensure total width of columnWidths matches currentElementWidth if possible
    // This might require scaling, but for now, let's keep it simple.
    // This is a compound update because we are changing both the headers and the data structure
    const newAdvancedTableData = {
      ...advancedTableProps,
      headers: newHeaders,
      numColumns: newNumColumns,
      columnWidths: newColumnWidths,
      data: newTableData,
    };

    updateElementProperty('advancedTableData', newAdvancedTableData);
  };

  const handleColumnReorder = (reorderedHeaders: AdvancedTableHeader[]) => {
    if (!advancedTableProps) return;

    const oldHeaders = advancedTableProps.headers || [];
    const oldColumnWidths = advancedTableProps.columnWidths || [];
    const oldData = advancedTableProps.data || [];

    // Create a map of old header keys to their original index for easier lookup
    const oldHeaderKeyToIndexMap = new Map<string, number>();
    oldHeaders.forEach((header, index) => {
      oldHeaderKeyToIndexMap.set(header.key, index);
    });

    // Reorder columnWidths
    const newColumnWidths = reorderedHeaders.map((header) => {
      const oldIndex = oldHeaderKeyToIndexMap.get(header.key);
      // If an old header doesn't exist (should not happen if reordering existing ones),
      // or if columnWidths array was somehow shorter, provide a default or handle error.
      // For simplicity, assuming oldIndex will be found and oldColumnWidths is aligned.
      return oldIndex !== undefined && oldIndex < oldColumnWidths.length
        ? oldColumnWidths[oldIndex]
        : (currentElementWidth || 100) / (reorderedHeaders.length || 1);
    });

    // Reorder data (each row's cells)
    const newData = oldData.map((row) => {
      return reorderedHeaders.map((header) => {
        const oldIndex = oldHeaderKeyToIndexMap.get(header.key);
        // Similar to columnWidths, assuming oldIndex will be found and row is aligned.
        return oldIndex !== undefined && oldIndex < row.length
          ? row[oldIndex]
          : { content: '' }; // Default cell for safety
      });
    });

    const newAdvancedTableData = {
      ...advancedTableProps,
      headers: reorderedHeaders,
      columnWidths: newColumnWidths,
      data: newData,
    };

    updateElementProperty('advancedTableData', newAdvancedTableData);
  };

  // Calculate the value for the Autocomplete, selecting the closest percentage option
  const autocompleteValue = (() => {
    if (
      !canvasWidth ||
      !currentElementWidth ||
      canvasWidth <= 0 ||
      widthPercentageOptions.length === 0
    ) {
      return null; // Not enough info or no options to select from
    }

    const currentActualPercentage = (currentElementWidth / canvasWidth) * 100;

    // Find the option whose value is closest to currentActualPercentage
    // Assumes widthPercentageOptions is not empty, which it is by definition here.
    return widthPercentageOptions.reduce((prev, curr) => {
      const prevDiff = Math.abs(prev.value - currentActualPercentage);
      const currDiff = Math.abs(curr.value - currentActualPercentage);
      // If current option is closer, it wins. In case of a tie, the one earlier in the list (prev) wins.
      return currDiff < prevDiff ? curr : prev;
    });
  })();

  // Ensure advancedTableProps exists before trying to access its properties
  if (!advancedTableProps) {
    return <Box sx={{ p: 2 }}>No table selected or data unavailable.</Box>;
  }

  return (
    <>
      <Box>
        <Accordion square className="no-bottom-border" defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0 }}>
            <Typography variant="subtitle2">Table Settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mt: 2 }}>
              <Autocomplete
                label="Table Width"
                options={widthPercentageOptions}
                value={autocompleteValue} // This is the prop passed to Autocomplete
                onChange={(event, newValue) =>
                  handleWidthPercentageChange(newValue)
                }
                getOptionLabel={(option: { label: string; value: number }) =>
                  option.label
                }
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                disabled={!canvasWidth}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <MultiSelect
                label="Select Columns"
                options={AVAILABLE_ADVANCED_TABLE_COLUMNS}
                values={advancedTableProps.headers || []} // Pass the array of header objects
                onChange={(selectedOptions) => {
                  handleColumnSelectionChange(
                    selectedOptions as AdvancedTableHeader[] | null
                  );
                }}
                getOptionLabel={(option: AdvancedTableHeader) => option.label} // How to display option text
                error={false} // Example, manage actual error state if needed
                maxChipsToShow={1} // Set max chips to display to 1
                dataQa="advanced-table-column-select"
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!advancedTableProps.showGrid} // Use !! to ensure boolean
                    onChange={handleShowGridChange}
                  />
                }
                label="Show Grid"
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion square className="no-bottom-border">
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0 }}>
            <Typography variant="subtitle2">Header Styles</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CompactColorPicker
              label="Header Text"
              color={advancedTableProps.headerTextColor || '#000000'}
              onChange={handleHeaderTextColorChange}
            />
            <CompactColorPicker
              label="Header Background"
              color={advancedTableProps.headerBackgroundColor || '#f0f0f0'}
              onChange={handleHeaderBackgroundColorChange}
            />
            <Box sx={{ mt: 2 }}>
              <TextField
                className="redesign"
                variant="standard"
                label="Header Font Size"
                type="number"
                value={localHeaderFontSize}
                onChange={(e) => handleFontSizeChange(e, true)}
                fullWidth
                InputProps={{
                  inputProps: { min: 0 }, // Minimum font size, allow 0
                }}
                size="small"
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Autocomplete
                label="Header Font Family"
                options={FONT_FAMILY_OPTIONS}
                value={
                  FONT_FAMILY_OPTIONS.find(
                    (option) =>
                      option.value === advancedTableProps.headerFontFamily
                  ) || null
                }
                onChange={(event, newValue) =>
                  handleHeaderFontFamilyChange(newValue)
                }
                getOptionLabel={(option: { label: string; value: string }) =>
                  option.label
                }
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion square className="no-bottom-border">
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0 }}>
            <Typography variant="subtitle2">Cell Styles</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CompactColorPicker
              label="Cell Text"
              color={advancedTableProps.defaultCellTextColor || '#333333'}
              onChange={handleCellTextColorChange}
            />
            <CompactColorPicker
              label="Cell Background"
              color={advancedTableProps.defaultCellBackgroundColor || '#ffffff'}
              onChange={handleCellBackgroundColorChange}
            />
            <Box sx={{ mt: 2 }}>
              <TextField
                className="redesign"
                variant="standard"
                label="Cell Font Size"
                type="number"
                value={localCellFontSize}
                onChange={(e) => handleFontSizeChange(e, false)}
                fullWidth
                InputProps={{
                  inputProps: { min: 0 }, // Minimum font size, allow 0
                }}
                size="small"
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Autocomplete
                label="Cell Font Family"
                options={FONT_FAMILY_OPTIONS}
                value={
                  FONT_FAMILY_OPTIONS.find(
                    (option) =>
                      option.value === advancedTableProps.defaultCellFontFamily
                  ) || null
                }
                onChange={(event, newValue) =>
                  handleCellFontFamilyChange(newValue)
                }
                getOptionLabel={(option: { label: string; value: string }) =>
                  option.label
                }
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box>
        {advancedTableProps.headers &&
          advancedTableProps.headers.length > 1 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1.5 }}>
                Column Order
              </Typography>
              <DraggableColumnList
                columns={advancedTableProps.headers}
                onReorder={handleColumnReorder}
              />
            </Box>
          )}
      </Box>
    </>
  );
};

export default AdvancedTableProperties;
